import React, { useState, useCallback, useEffect } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Alert,
} from '@mui/material';

import { useApi } from '@/providers/ApiContext';
import {
  createAppClientKey,
  updateAppClientKey,
} from '@/actions/appClient';
import AccountKeyNameField from './AccountKeyNameField';
import type { AppClientKey, AppClientKeyExport } from '@/types';

interface AccountKeyFormProps {
  open: boolean;
  onClose: () => void;
  clientId: string | null;
  editingKey: AppClientKey | null;
  onKeyCreated?: (key: AppClientKeyExport) => void;
  onKeyUpdated?: (key: AppClientKey) => void;
}

function AccountKeyForm({
  open,
  onClose,
  clientId,
  editingKey,
  onKeyCreated,
  onKeyUpdated,
}: AccountKeyFormProps) {
  const api = useApi();
  const [name, setName] = useState<string>('');
  const [name_error, setNameError] = useState<string | boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [createdKey, setCreatedKey] = useState<
    AppClientKeyExport | { key: AppClientKeyExport } | null
  >(null);
  const [pristine, setPristine] = useState<boolean>(true);
  const isEditMode = Boolean(editingKey);

  // Populate form when editing
  useEffect(() => {
    if (editingKey) {
      setName(editingKey.name || '');
      setPristine(true);
    } else {
      setName('');
      setPristine(true);
    }
  }, [editingKey]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!api || !clientId) return;

      if (name_error || !name) {
        return;
      }

      try {
        setLoading(true);
        if (isEditMode && editingKey) {
          const updatedKey = await updateAppClientKey(
            api,
            clientId,
            editingKey.key_id,
            { name },
          );
          onKeyUpdated?.(updatedKey);
          onClose();
        } else {
          const newKey = await createAppClientKey(api, clientId, { name });
          setCreatedKey(newKey);
          onKeyCreated?.(newKey);
        }
      } catch (error) {
        console.error(
          'Error',
          isEditMode ? 'updating' : 'creating',
          'app client key:',
          error,
        );
        // You might want to add error handling here
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      clientId,
      name,
      name_error,
      isEditMode,
      editingKey,
      onKeyCreated,
      onKeyUpdated,
      onClose,
    ],
  );

  const handleDownloadKey = () => {
    if (!createdKey) return;

    // Handle both response structures: direct AppClientKeyExport or wrapped in { key: ... }
    const keyData =
      'key' in createdKey && createdKey.key
        ? createdKey.key
        : createdKey;

    if (!keyData || !('private_key' in keyData) || !keyData.private_key) {
      return;
    }

    const blob = new Blob([JSON.stringify(keyData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${keyData.key_id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setName('');
    setNameError(false);
    setCreatedKey(null);
    setPristine(true);
    onClose();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setPristine(false);
  };

  const handleNameError = (error: string | boolean) => {
    setNameError(error);
  };

  const handleChanged = () => {
    setPristine(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      aria-labelledby='create-key-dialog-title'
    >
      <DialogTitle id='create-key-dialog-title'>
        {isEditMode ? 'Edit API Key' : 'Create New API Key'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <AccountKeyNameField
                value={name}
                error={name_error}
                setValue={handleNameChange}
                setError={handleNameError}
                setChanged={handleChanged}
                disabled={Boolean(createdKey)}
              />
            </Grid>
          </Grid>
        </Box>

        {createdKey && (() => {
          const keyData: AppClientKeyExport =
            'key' in createdKey && createdKey.key
              ? createdKey.key
              : (createdKey as AppClientKeyExport);
          
          return (
            <Alert severity='info' sx={{ mt: 3 }}>
              <Typography variant='body1' gutterBottom>
                Your API key has been created successfully. Please download the
                key file now - this is the only time you will be able to access
                the private key.
              </Typography>

              <Typography variant='body2' sx={{ mt: 1, fontWeight: 'bold' }}>
                Treat this key file like a password - store it securely and never
                share it publicly.
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                The key file will be saved as:{' '}
                <strong>{keyData.key_id || 'key.json'}</strong>
              </Typography>
            </Alert>
          );
        })()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{createdKey ? 'Close' : 'Cancel'}</Button>
        {createdKey ? (
          <Button onClick={handleDownloadKey} variant='contained'>
            Download Key File
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant='contained'
            disabled={
              pristine || Boolean(name_error) || !Boolean(name) || loading
            }
          >
            {loading
              ? isEditMode
                ? 'Updating...'
                : 'Creating...'
              : isEditMode
                ? 'Update Key'
                : 'Create Key'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AccountKeyForm;

